import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import SearchableSelect from "../components/SearchableSelect";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Trash2, Pencil, FolderOpen, Power, KeyRound, Copy, Eye, EyeOff, Search, UserCircle2, User, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatDateFr } from "../lib/dateFormat";
import PasswordRulesChecklist, { isPasswordStrong } from "../components/PasswordRulesChecklist";
import HorizontalScrollContainer from "../components/HorizontalScrollContainer";
import StickyPageHeader from "../components/StickyPageHeader";
import RemunerationSection, { serializeRemuneration } from "../components/RemunerationSection";

const ROLES = [
  { value: "collaborateur", label: "Collaborateur" },
  { value: "gestionnaire", label: "Gestionnaire" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Administrateur" },
];

const ROLE_BADGES = {
  admin: { bg: "bg-zinc-900", text: "text-white" },
  manager: { bg: "bg-blue-50 border border-blue-200", text: "text-blue-700" },
  gestionnaire: { bg: "bg-violet-50 border border-violet-200", text: "text-violet-700" },
  collaborateur: { bg: "bg-zinc-50 border border-zinc-200", text: "text-zinc-700" },
};

// Indicatifs téléphoniques internationaux (les plus courants).
// Liste volontairement large mais bornée — extensible si besoin.
const PHONE_CODES = [
  { code: "+33", label: "France" },
  { code: "+32", label: "Belgique" },
  { code: "+41", label: "Suisse" },
  { code: "+352", label: "Luxembourg" },
  { code: "+377", label: "Monaco" },
  { code: "+44", label: "Royaume-Uni" },
  { code: "+49", label: "Allemagne" },
  { code: "+34", label: "Espagne" },
  { code: "+39", label: "Italie" },
  { code: "+351", label: "Portugal" },
  { code: "+31", label: "Pays-Bas" },
  { code: "+1", label: "USA / Canada" },
  { code: "+212", label: "Maroc" },
  { code: "+213", label: "Algérie" },
  { code: "+216", label: "Tunisie" },
  { code: "+221", label: "Sénégal" },
  { code: "+225", label: "Côte d'Ivoire" },
  { code: "+226", label: "Burkina Faso" },
  { code: "+227", label: "Niger" },
  { code: "+228", label: "Togo" },
  { code: "+229", label: "Bénin" },
  { code: "+233", label: "Ghana" },
  { code: "+234", label: "Nigeria" },
  { code: "+235", label: "Tchad" },
  { code: "+237", label: "Cameroun" },
  { code: "+241", label: "Gabon" },
  { code: "+242", label: "Congo" },
  { code: "+243", label: "RD Congo" },
  { code: "+261", label: "Madagascar" },
  { code: "+262", label: "Réunion / Mayotte" },
  { code: "+590", label: "Guadeloupe" },
  { code: "+594", label: "Guyane" },
  { code: "+596", label: "Martinique" },
];

export default function Collaborateurs() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    first_name: "",
    last_name: "",
    role: "collaborateur",
    category_id: "",
    brand_ids: [],
    contract_start: "",
    contract_end: "",
    is_active: true,
    date_naissance: "",
    poste: "",
    id_immatriculation: "",
    phone_country_code: "+33",
    phone_number: "",
    remuneration_fixed_ht: "",
    remuneration_variable_mode: "none",
    remuneration_variable_percentage: "",
    remuneration_variable_amount: "",
    remuneration_notes: "",
  });
  const [formTab, setFormTab] = useState("info");

  const canManage = user?.role === "admin" || user?.role === "manager";
  const isAdmin = user?.role === "admin";

  // RBAC iter107 : depuis la liste des collaborateurs, un manager n'a
  // aucun droit de consultation ni de modification du dossier d'un
  // profil « direction » (rôles Admin/Manager, ou catégorie flag
  // `is_leadership`). Les icônes Dossier + Éditer sont alors masquées.
  const isManagerProtectedRow = (c) => {
    if (user?.role !== "manager") return false;
    if (c.id === user?.id) return false;
    if (["admin", "manager"].includes(c.role)) return true;
    const cat = categories.find((x) => x.id === c.category_id);
    return !!cat?.is_leadership;
  };

  // La catégorie sélectionnée dans le formulaire détermine si l'onglet
  // « Rémunération » doit être affiché (règle métier : ce module ne
  // concerne que les non-salariés — cf. Bulletins de rémunération).
  const selectedCategory = categories.find((c) => c.id === form.category_id);
  const isSalarieCategory = !!selectedCategory?.is_salarie;
  const showRemunTab = isAdmin && !isSalarieCategory;

  // Si l'utilisateur passe d'une catégorie non-salariée à une catégorie
  // salariée alors qu'il est encore sur l'onglet Rémunération, on le
  // ramène automatiquement sur l'onglet Informations.
  useEffect(() => {
    if (!showRemunTab && formTab === "remuneration") setFormTab("info");
  }, [showRemunTab, formTab]);

  // Liste filtrée par recherche + triée alphabétiquement par nom (insensible aux accents/casse).
  const sortedFilteredList = (() => {
    const q = search.trim().toLowerCase();
    const matches = q
      ? list.filter((c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.poste || "").toLowerCase().includes(q)
        )
      : list;
    return [...matches].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" })
    );
  })();

  const load = async () => {
    const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
    const [u, c] = await Promise.all([
      api.get("/collaborateurs", { params }),
      api.get("/categories"),
    ]);
    setList(u.data || []);
    setCategories(c.data || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedBrandId]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      email: "", password: "", name: "", first_name: "", last_name: "", role: "collaborateur", category_id: "", brand_ids: [],
      contract_start: "", contract_end: "", is_active: true, date_naissance: "", poste: "",
      id_immatriculation: "",
      phone_country_code: "+33", phone_number: "",
      remuneration_fixed_ht: "", remuneration_variable_mode: "none",
      remuneration_variable_percentage: "", remuneration_variable_amount: "", remuneration_notes: "",
    });
    setFormTab("info");
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    const mode = c.remuneration_variable_mode && c.remuneration_variable_mode !== ""
      ? c.remuneration_variable_mode
      : "none";
    setForm({
      email: c.email,
      password: "",
      name: c.name,
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      role: c.role,
      category_id: c.category_id || "",
      brand_ids: c.brand_ids || [],
      contract_start: c.contract_start || "",
      contract_end: c.contract_end || "",
      is_active: c.is_active !== false,
      date_naissance: c.date_naissance || "",
      poste: c.poste || "",
      id_immatriculation: c.id_immatriculation || "",
      phone_country_code: c.phone_country_code || "+33",
      phone_number: c.phone_number || "",
      remuneration_fixed_ht: c.remuneration_fixed_ht ?? "",
      remuneration_variable_mode: mode,
      remuneration_variable_percentage: c.remuneration_variable_percentage ?? "",
      remuneration_variable_amount: c.remuneration_variable_amount ?? "",
      remuneration_notes: c.remuneration_notes || "",
    });
    setFormTab("info");
    setOpen(true);
  };

  const save = async () => {
    try {
      // Compose automatiquement `name` (Prénom NOM) à partir des 2 champs
      // séparés. Si l'utilisateur n'a saisi qu'un des deux, on garde ce seul
      // fragment. Le champ `name` reste utilisé pour l'affichage historique
      // partout dans l'app (planning, transactions, PDF, etc.).
      const fn = (form.first_name || "").trim();
      const ln = (form.last_name || "").trim();
      const composedName = `${fn} ${ln}`.trim() || form.name.trim();
      // Ne pas envoyer la config rémunération pour un profil salarié
      // (le module « Bulletins de rémunération » ne les concerne pas).
      const remunPayload = isAdmin && !isSalarieCategory ? serializeRemuneration(form) : {};
      if (editing) {
        await api.put(`/collaborateurs/${editing.id}`, {
          name: composedName,
          first_name: fn || null,
          last_name: ln || null,
          role: form.role,
          category_id: form.category_id || null,
          brand_ids: form.brand_ids,
          contract_start: form.contract_start || null,
          contract_end: form.contract_end || null,
          is_active: form.is_active,
          date_naissance: form.date_naissance || null,
          poste: form.poste || "",
          id_immatriculation: (form.id_immatriculation || "").trim(),
          phone_country_code: form.phone_number ? form.phone_country_code : null,
          phone_number: form.phone_number || null,
          ...remunPayload,
        });
        toast.success("Collaborateur mis à jour");
      } else {
        if (!form.email || !form.password || (!fn && !ln)) {
          toast.error("Email, mot de passe et au moins un nom ou prénom sont requis");
          return;
        }
        if (!isPasswordStrong(form.password)) {
          toast.error("Le mot de passe ne respecte pas la politique de sécurité");
          return;
        }
        await api.post("/collaborateurs", {
          email: form.email,
          password: form.password,
          name: composedName,
          first_name: fn || null,
          last_name: ln || null,
          role: form.role,
          category_id: form.category_id || null,
          brand_ids: form.brand_ids,
          contract_start: form.contract_start || null,
          contract_end: form.contract_end || null,
          is_active: form.is_active,
          date_naissance: form.date_naissance || null,
          poste: form.poste || "",
          id_immatriculation: (form.id_immatriculation || "").trim(),
          phone_country_code: form.phone_number ? form.phone_country_code : null,
          phone_number: form.phone_number || null,
          ...remunPayload,
        });
        toast.success("Collaborateur créé");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/collaborateurs/${c.id}`, { is_active: !(c.is_active !== false) });
      toast.success(c.is_active === false ? "Collaborateur réactivé" : "Collaborateur désactivé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce collaborateur ?")) return;
    await api.delete(`/collaborateurs/${id}`);
    toast.success("Supprimé");
    load();
  };

  // --- Réinitialisation mot de passe (admin uniquement) ---
  const [resetInfo, setResetInfo] = useState(null); // { name, email, temporary_password }
  const [resetting, setResetting] = useState(null); // id en cours

  const resetPassword = async (c) => {
    if (!window.confirm(`Générer un nouveau mot de passe temporaire pour ${c.name} ?\nL'ancien mot de passe sera invalidé immédiatement.`)) return;
    try {
      setResetting(c.id);
      const { data } = await api.post(`/collaborateurs/${c.id}/reset-password`);
      setResetInfo({
        name: data.collaborateur_name,
        email: data.collaborateur_email,
        temporary_password: data.temporary_password,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setResetting(null);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copié dans le presse-papiers");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const toggleBrand = (bid) => {
    setForm((f) => ({
      ...f,
      brand_ids: f.brand_ids.includes(bid)
        ? f.brand_ids.filter((x) => x !== bid)
        : [...f.brand_ids, bid],
    }));
  };

  return (
    <div className="space-y-6" data-testid="collaborateurs-page">
      <StickyPageHeader testid="collab-sticky-header">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-uppercase mb-2">Effectif</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
            Collaborateurs
          </h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">{list.length} collaborateur(s)</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm" data-testid="new-collab-btn">
                <Plus size={14} className="mr-1" /> Nouveau collaborateur
              </Button>
            </DialogTrigger>
            <DialogContent className="flex flex-col max-h-[90vh] p-0">
              <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
                <DialogTitle>{editing ? "Modifier le collaborateur" : "Nouveau collaborateur"}</DialogTitle>
              </DialogHeader>
              <Tabs value={formTab} onValueChange={setFormTab} className="flex flex-col flex-1 min-h-0">
                <div className="px-6 pt-3 border-b border-zinc-100 bg-zinc-50">
                  <TabsList className="bg-transparent p-0 h-auto gap-1">
                    <TabsTrigger value="info" className="rounded-sm text-xs px-3 py-1.5" data-testid="collab-tab-info">
                      <User size={12} className="mr-1.5" /> Informations
                    </TabsTrigger>
                    {showRemunTab && (
                      <TabsTrigger value="remuneration" className="rounded-sm text-xs px-3 py-1.5" data-testid="collab-tab-remuneration">
                        <Wallet size={12} className="mr-1.5" /> Rémunération
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>
                <TabsContent value="info" className="mt-0 flex-1 overflow-y-auto" data-testid="collab-tab-info-content">
                  <div className="space-y-4 px-6 py-4" data-testid="collab-dialog-scroll">
                {!editing && (
                  <>
                    {/* Honeypot anti-autofill : leurre invisible pour absorber l'auto-complétion navigateur. */}
                    <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
                      <input type="text" name="fakeusernameremembered" tabIndex={-1} autoComplete="username" />
                      <input type="password" name="fakepasswordremembered" tabIndex={-1} autoComplete="current-password" />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Email</Label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="collab-email"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        name="new-collab-email"
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Mot de passe</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={form.password}
                          onChange={(e) => setForm({ ...form, password: e.target.value })}
                          className="pr-11"
                          data-testid="collab-password"
                          autoComplete="new-password"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          name="new-collab-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                          title={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                          className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-500 hover:text-zinc-800 transition-colors"
                          data-testid="collab-password-toggle"
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <div className="mt-2 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                        <p className="label-uppercase mb-1.5 text-zinc-600">Politique de sécurité</p>
                        <PasswordRulesChecklist password={form.password} />
                      </div>
                    </div>
                  </>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Nom <span className="text-red-500">*</span></Label>
                    <Input
                      value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      placeholder="DUPONT"
                      data-testid="collab-last-name"
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Prénom(s) <span className="text-red-500">*</span></Label>
                    <Input
                      value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      placeholder="Jean-Marc"
                      data-testid="collab-first-name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Rôle</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="collab-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Catégorie</Label>
                    <SearchableSelect
                      value={form.category_id || ""}
                      onChange={(v) => setForm({ ...form, category_id: v })}
                      options={categories.map((c) => ({
                        value: c.id,
                        label: c.name,
                        keywords: c.name || "",
                      }))}
                      placeholder="Aucune"
                      searchPlaceholder="Rechercher une catégorie…"
                      emptyLabel="Aucune catégorie trouvée."
                      allowClear
                      data-testid="collab-category"
                    />
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Départements</Label>
                  <div className="space-y-2 border border-zinc-200 rounded-sm p-3 max-h-40 overflow-y-auto">
                    {brands.map((b) => (
                      <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={form.brand_ids.includes(b.id)}
                          onCheckedChange={() => toggleBrand(b.id)}
                          data-testid={`collab-brand-${b.id}`}
                        />
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
                        {b.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Début de contrat</Label>
                    <Input type="date" value={form.contract_start} onChange={(e) => setForm({ ...form, contract_start: e.target.value })} data-testid="collab-contract-start" />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Fin de contrat</Label>
                    <Input type="date" value={form.contract_end} onChange={(e) => setForm({ ...form, contract_end: e.target.value })} data-testid="collab-contract-end" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Date de naissance</Label>
                    <Input type="date" value={form.date_naissance} onChange={(e) => setForm({ ...form, date_naissance: e.target.value })} data-testid="collab-date-naissance" />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Poste / Fonction</Label>
                    <Input value={form.poste} onChange={(e) => setForm({ ...form, poste: e.target.value })} placeholder="Ex: Serveur, Caissier, Chef de chantier…" data-testid="collab-poste" />
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">N° ID immatriculation</Label>
                  <Input
                    value={form.id_immatriculation}
                    onChange={(e) => setForm({ ...form, id_immatriculation: e.target.value })}
                    placeholder="Ex : n° URSSAF, n° MSA, matricule interne…"
                    data-testid="collab-id-immatriculation"
                  />
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Téléphone</Label>
                  <div className="flex gap-2">
                    <SearchableSelect
                      value={form.phone_country_code}
                      onChange={(v) => setForm({ ...form, phone_country_code: v })}
                      options={PHONE_CODES.map((p) => ({
                        value: p.code,
                        label: `${p.code} — ${p.label}`,
                        keywords: `${p.code} ${p.label}`,
                      }))}
                      searchPlaceholder="Code ou pays…"
                      emptyLabel="Aucun indicatif trouvé."
                      className="w-40"
                      data-testid="collab-phone-code"
                    />
                    <Input
                      type="tel"
                      value={form.phone_number}
                      onChange={(e) => setForm({ ...form, phone_number: e.target.value.replace(/[^0-9\s.-]/g, "") })}
                      placeholder="6 12 34 56 78"
                      data-testid="collab-phone-number"
                      className="flex-1"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={form.is_active}
                    onCheckedChange={(v) => setForm({ ...form, is_active: !!v })}
                    data-testid="collab-active"
                  />
                  <span>Compte actif (peut se connecter)</span>
                </label>
              </div>
                </TabsContent>
                {isAdmin && (
                  <TabsContent value="remuneration" className="mt-0 flex-1 overflow-y-auto" data-testid="collab-tab-remuneration-content">
                    <div className="px-6 py-4">
                      <RemunerationSection form={form} setForm={setForm} />
                    </div>
                  </TabsContent>
                )}
              </Tabs>
              <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="collab-save-btn">Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-3 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <Label className="label-uppercase mb-1.5 block">Recherche</Label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              placeholder="Nom, email ou poste…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="collab-search"
            />
          </div>
        </div>
        <p className="text-xs text-zinc-500 font-mono pb-2" data-testid="collab-count">
          {sortedFilteredList.length} / {list.length} résultat(s)
        </p>
      </div>
      </StickyPageHeader>

      <HorizontalScrollContainer testId="collab-table-scroll" className="bg-white border border-zinc-200 rounded-sm">
        <table className="w-full min-w-[840px]">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left label-uppercase p-3">Nom</th>
              <th className="text-left label-uppercase p-3">Prénom(s)</th>
              <th className="text-left label-uppercase p-3">Email</th>
              <th className="text-left label-uppercase p-3">Téléphone</th>
              <th className="text-left label-uppercase p-3">Rôle</th>
              <th className="text-left label-uppercase p-3">Poste</th>
              <th className="text-left label-uppercase p-3">N° ID immatriculation</th>
              <th className="text-left label-uppercase p-3">Catégorie</th>
              <th className="text-left label-uppercase p-3">Départements</th>
              <th className="text-left label-uppercase p-3">Contrat</th>
              <th className="text-left label-uppercase p-3">Statut</th>
              {canManage && <th className="text-right label-uppercase p-3">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedFilteredList.map((c) => {
              const cat = categories.find((x) => x.id === c.category_id);
              const badge = ROLE_BADGES[c.role] || ROLE_BADGES.collaborateur;
              return (
                <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`collab-row-${c.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2.5">
                      <UserCircle2 className="h-7 w-7 text-blue-600 shrink-0" strokeWidth={1.5} data-testid={`collab-icon-${c.id}`} />
                      <span className="text-sm font-medium text-zinc-900" data-testid={`collab-last-name-${c.id}`}>
                        {c.last_name || (c.first_name ? "" : c.name)}
                      </span>
                    </div>
                  </td>
                  <td className="p-3 text-sm text-zinc-700" data-testid={`collab-first-name-${c.id}`}>
                    {c.first_name || <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="p-3 font-mono text-xs text-zinc-600">{c.email}</td>
                  <td className="p-3 font-mono text-xs text-zinc-700" data-testid={`phone-${c.id}`}>
                    {c.phone_number ? (
                      <a href={`tel:${(c.phone_country_code || "")}${c.phone_number.replace(/\s/g, "")}`} className="text-zinc-700 hover:text-[#0055FF]">
                        <span className="text-zinc-400">{c.phone_country_code || ""}</span> {c.phone_number}
                      </a>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase font-semibold px-2 py-1 rounded-sm tracking-wider ${badge.bg} ${badge.text}`}>
                      {ROLES.find((r) => r.value === c.role)?.label || c.role}
                    </span>
                  </td>
                  <td className="p-3 text-sm text-zinc-700" data-testid={`poste-${c.id}`}>{c.poste || <span className="text-zinc-400">—</span>}</td>
                  <td className="p-3 font-mono text-xs text-zinc-700" data-testid={`id-immatriculation-${c.id}`}>
                    {c.id_immatriculation ? c.id_immatriculation : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="p-3 text-sm text-zinc-700">{cat?.name || "—"}</td>
                  <td className="p-3">
                    <div className="flex gap-1 flex-wrap">
                      {(c.brand_ids || []).map((bid) => {
                        const b = brands.find((x) => x.id === bid);
                        if (!b) return null;
                        return (
                          <span key={bid} className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 bg-zinc-50 border border-zinc-200 rounded-sm">
                            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: b.color }} />
                            {b.name}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="p-3 font-mono text-[11px] text-zinc-600">
                    {c.contract_start || c.contract_end ? (
                      <span>{formatDateFr(c.contract_start) || "—"} → {formatDateFr(c.contract_end) || "—"}</span>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    {c.is_active === false ? (
                      <span className="text-[10px] uppercase font-semibold px-2 py-1 rounded-sm tracking-wider bg-red-50 border border-red-200 text-red-700" data-testid={`status-inactive-${c.id}`}>
                        Inactif
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase font-semibold px-2 py-1 rounded-sm tracking-wider bg-emerald-50 border border-emerald-200 text-emerald-700" data-testid={`status-active-${c.id}`}>
                        Actif
                      </span>
                    )}
                  </td>
                  {canManage && (
                    <td className="p-3 text-right">
                      {isManagerProtectedRow(c) ? (
                        <span className="text-[10px] uppercase font-mono text-zinc-400 tracking-wider" data-testid={`protected-row-${c.id}`} title="Profil de direction — dossier réservé à l'administrateur">
                          Protégé
                        </span>
                      ) : (
                        <>
                      <Link
                        to={`/collaborateurs/${c.id}/dossier`}
                        className="inline-flex items-center justify-center h-8 w-8 hover:bg-zinc-100 rounded-sm text-zinc-600"
                        title="Dossier"
                        data-testid={`open-dossier-${c.id}`}
                      >
                        <FolderOpen size={14} />
                      </Link>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-${c.id}`}>
                        <Pencil size={14} />
                      </Button>
                        </>
                      )}
                      {user?.role === "admin" && c.id !== user?.id && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600"
                            onClick={() => resetPassword(c)}
                            disabled={resetting === c.id}
                            title="Réinitialiser le mot de passe"
                            data-testid={`reset-password-${c.id}`}
                          >
                            <KeyRound size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={c.is_active === false ? "text-emerald-600" : "text-amber-600"}
                            onClick={() => toggleActive(c)}
                            title={c.is_active === false ? "Réactiver" : "Désactiver"}
                            data-testid={`toggle-active-${c.id}`}
                          >
                            <Power size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(c.id)} data-testid={`delete-${c.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {sortedFilteredList.length === 0 && (
              <tr>
                <td colSpan={canManage ? 11 : 10} className="text-center p-8 text-sm text-zinc-500 font-mono">
                  {list.length === 0 ? "Aucun collaborateur" : "Aucun résultat pour cette recherche"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </HorizontalScrollContainer>

      {/* Modale d'affichage du mot de passe temporaire (admin) */}
      <Dialog open={Boolean(resetInfo)} onOpenChange={(o) => !o && setResetInfo(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Mot de passe temporaire généré</DialogTitle>
          </DialogHeader>
          {resetInfo && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-600">
                Communiquez ce mot de passe à <strong>{resetInfo.name}</strong> ({resetInfo.email}).
                Il ne sera <strong>pas</strong> affiché à nouveau.
              </p>
              <div className="flex items-center gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                <code
                  className="flex-1 font-mono text-sm text-zinc-900 select-all break-all"
                  data-testid="temporary-password-value"
                >
                  {resetInfo.temporary_password}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(resetInfo.temporary_password)}
                  data-testid="copy-temporary-password"
                >
                  <Copy size={14} className="mr-1" /> Copier
                </Button>
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                ⚠️ L&apos;ancien mot de passe est désormais invalide. Le collaborateur devra
                définir un nouveau mot de passe lors de sa prochaine connexion.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResetInfo(null)} data-testid="close-reset-dialog">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
